import jsPDF from "jspdf";
import moment from "moment";

// Helper function to load image and convert to base64
const loadImageAsBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const base64 = canvas.toDataURL('image/png');
        resolve(base64);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    
    // Set timeout for loading
    setTimeout(() => reject(new Error('Image loading timeout')), 3000);
    
    img.src = url;
  });
};

// Function to generate delivery note PDF
export const generateDeliveryNotePDF = async (orderDetails, user) => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.width;
  const pageHeight = pdf.internal.pageSize.height;
  let yPos = 10; // Reduced from 20 to 10

  // Business info
  const businessName = user.business_name || (user.employer && user.employer.business_name) || 'Company Name';
  const businessAddress = user.address_line1 || 'Your Location Here';
  const businessEmail = user.email || 'info@yourdomain.com';
  const businessPhone = user.contact_number || '+001 123 1234';
  
  // Order details
  const orderNumber = orderDetails.orderNumber || 'N/A';
  const orderDate = orderDetails.orderDate ? moment(orderDetails.orderDate).format("MM/DD/YY") : moment().format("MM/DD/YY");

  // Check for business logo
  const logoUrl = user.image || (user.employer && user.employer.image);
  let logoWidth = 0;
  
  // Add logo if available
  if (logoUrl) {
    logoWidth = 40;
    const logoHeight = 30;
    
    try {
      console.log("Loading logo from:", logoUrl);
      const base64Image = await loadImageAsBase64(logoUrl);
      pdf.addImage(base64Image, 'PNG', 20, yPos, logoWidth, logoHeight);
      console.log("Logo added successfully");
    } catch (error) {
      console.log("Could not load logo, using placeholder:", error);
      // Fallback to placeholder if image fails
      pdf.setDrawColor(200);
      pdf.rect(20, yPos, logoWidth, logoHeight);
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text('LOGO', 35, yPos + 18);
      pdf.setTextColor(0, 0, 0); // Reset text color
    }
  }

  // Left side - Company info (adjust position if logo exists)
  const companyInfoX = logoUrl ? 20 + logoWidth + 10 : 20;
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text(businessName, companyInfoX, yPos + 5);
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Address: ${businessAddress}`, companyInfoX, yPos + 15);
  pdf.text(`Email: ${businessEmail}`, companyInfoX, yPos + 23);
  pdf.text(`Phone: ${businessPhone}`, companyInfoX, yPos + 31);

  // Right side - Business name in brackets (smaller font)
  pdf.setFontSize(18); // Reduced from 24 to 18
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(217, 0, 104); // Loystar primary color #D90068
  pdf.text(`[${businessName}]`, pageWidth - 20, yPos + 15, { align: 'right' });
  pdf.setTextColor(0, 0, 0); // Reset to black

  yPos += 45;

  // "Delivery to" section
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Delivery to', 20, yPos);
  yPos += 8;

  // Customer info box (left side)
  pdf.setDrawColor(200, 200, 200);
  pdf.setFillColor(248, 248, 248);
  pdf.roundedRect(20, yPos, 100, 30, 3, 3, 'FD'); // Reduced height from 35 to 30, width from 125 to 100
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  
  // Customer name - ensure it's properly extracted
  const customerName = orderDetails.customer?.name || 
                      `${orderDetails.customer?.first_name || ''} ${orderDetails.customer?.last_name || ''}`.trim() ||
                      'Customer Name';
  pdf.text(`Name: ${customerName}`, 25, yPos + 8);
  
  // Use delivery address from order details - prioritize delivery address over customer address
  const deliveryAddress = orderDetails.customer?.address || 
                         orderDetails.deliveryAddress || 
                         orderDetails.delivery_address ||
                         'Delivery address not provided';
  const addressLines = pdf.splitTextToSize(`Address: ${deliveryAddress}`, 90);
  let addressY = yPos + 16;
  addressLines.forEach(line => {
    pdf.text(line, 25, addressY);
    addressY += 5;
  });

  // Date info box (right side)
  pdf.setDrawColor(200, 200, 200);
  pdf.setFillColor(248, 248, 248);
  pdf.roundedRect(pageWidth - 70, yPos, 50, 30, 3, 3, 'FD'); // Date column box
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Date:', pageWidth - 65, yPos + 8);
  pdf.text('Due Date:', pageWidth - 65, yPos + 18);
  
  pdf.setFont('helvetica', 'normal');
  pdf.text(orderDate, pageWidth - 25, yPos + 8, { align: 'right' });
  pdf.text(orderDate, pageWidth - 25, yPos + 18, { align: 'right' });

  yPos += 35; // Reduced from 40 to 35 since boxes are now shorter

  // Items table
  const tableStartY = yPos;
  const tableHeight = 130; // Increased from 120 to 130 to use more space
  const colWidths = [30, 80, 30, 40]; // Item No, Description, QTY, Complete
  let tableX = 20;

  // Function to add table header
  const addTableHeader = (startY) => {
    pdf.setFillColor(217, 0, 104); // Loystar primary color #D90068
    pdf.setTextColor(255, 255, 255); // White text
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);

    // Header background
    pdf.rect(tableX, startY, colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], 12, 'F');

    // Header text
    pdf.text('Item No', tableX + 5, startY + 8);
    pdf.text('Description', tableX + colWidths[0] + 5, startY + 8);
    pdf.text('QTY', tableX + colWidths[0] + colWidths[1] + 5, startY + 8);
    pdf.text('Complete', tableX + colWidths[0] + colWidths[1] + colWidths[2] + 5, startY + 8);

    // Reset text color
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');
  };

  // Function to add table border
  const addTableBorder = (startY, height) => {
    pdf.setDrawColor(200, 200, 200);
    pdf.rect(tableX, startY, colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], height);

    // Vertical lines
    pdf.line(tableX + colWidths[0], startY, tableX + colWidths[0], startY + height);
    pdf.line(tableX + colWidths[0] + colWidths[1], startY, tableX + colWidths[0] + colWidths[1], startY + height);
    pdf.line(tableX + colWidths[0] + colWidths[1] + colWidths[2], startY, tableX + colWidths[0] + colWidths[1] + colWidths[2], startY + height);
  };

  // Initial table setup
  addTableHeader(tableStartY);
  addTableBorder(tableStartY, tableHeight);

  // Fill in actual items with pagination
  let itemY = tableStartY + 20;
  const rowHeight = 12;
  const maxItemsPerPage = Math.floor((tableHeight - 20) / rowHeight);
  let currentPage = 1;
  let itemsOnCurrentPage = 0;
  
  if (orderDetails.items && orderDetails.items.length > 0) {
    orderDetails.items.forEach((item, index) => {
      // Check if we need a new page
      if (itemsOnCurrentPage >= maxItemsPerPage) {
        // Add footer for current page
        const footerY = pageHeight - 10;
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Page ${currentPage}`, pageWidth / 2, footerY, { align: 'center' });
        
        // Add new page
        pdf.addPage();
        currentPage++;
        itemsOnCurrentPage = 0;
        
        // Reset position for new page
        itemY = 20; // Start from top of new page
        const newTableStartY = itemY;
        const newTableHeight = pageHeight - 60; // Leave space for footer and signature
        
        // Add table header and border for new page
        addTableHeader(newTableStartY);
        addTableBorder(newTableStartY, newTableHeight);
        
        itemY = newTableStartY + 20;
      }
      
      // Item number
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      pdf.text((index + 1).toString(), tableX + 5, itemY);
      
      // Description (with text wrapping)
      const maxDescWidth = colWidths[1] - 10;
      const descLines = pdf.splitTextToSize(item.name || 'Item', maxDescWidth);
      pdf.text(descLines[0], tableX + colWidths[0] + 5, itemY);
      
      // Quantity
      pdf.text((item.quantity || 1).toString(), tableX + colWidths[0] + colWidths[1] + 5, itemY);
      
      // Complete checkbox (empty square)
      const checkboxX = tableX + colWidths[0] + colWidths[1] + colWidths[2] + 15;
      const checkboxY = itemY - 5;
      pdf.setDrawColor(100, 100, 100);
      pdf.rect(checkboxX, checkboxY, 6, 6);
      pdf.setDrawColor(200, 200, 200); // Reset
      
      itemY += rowHeight;
      itemsOnCurrentPage++;
      
      // Add additional description lines if needed
      if (descLines.length > 1) {
        for (let i = 1; i < descLines.length; i++) {
          // Check if we need a new page for description lines
          if (itemsOnCurrentPage >= maxItemsPerPage) break;
          
          pdf.text(descLines[i], tableX + colWidths[0] + 5, itemY);
          itemY += 8;
          itemsOnCurrentPage += 0.5; // Count partial rows
        }
      }
    });
  } else {
    // If no items, show placeholder
    pdf.text('1', tableX + 5, itemY);
    pdf.text('No items specified', tableX + colWidths[0] + 5, itemY);
    pdf.text('1', tableX + colWidths[0] + colWidths[1] + 5, itemY);
    
    const checkboxX = tableX + colWidths[0] + colWidths[1] + colWidths[2] + 15;
    const checkboxY = itemY - 5;
    pdf.setDrawColor(100, 100, 100);
    pdf.rect(checkboxX, checkboxY, 6, 6);
    pdf.setDrawColor(200, 200, 200);
    itemY += rowHeight;
  }

  // Add horizontal lines for remaining empty rows on current page
  const currentTableBottom = currentPage === 1 ? tableStartY + tableHeight : pageHeight - 60;
  while (itemY < currentTableBottom - 5) {
    pdf.setDrawColor(230, 230, 230);
    pdf.line(tableX, itemY, tableX + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], itemY);
    itemY += rowHeight;
  }

  // Position for signature (always on last page)
  yPos = currentTableBottom + 15;

  // Signature section
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Signature:', 20, yPos);
  pdf.text('Terms & Conditions:', pageWidth / 2 + 10, yPos);
  
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  
  // Signature line
  pdf.line(70, yPos, 150, yPos);
  
  // Terms text - replace Lorem ipsum with relevant content
  const termsText = 'Please inspect all items upon delivery. Report any discrepancies immediately. Signature confirms receipt of items in satisfactory condition.';
  const termsLines = pdf.splitTextToSize(termsText, 80);
  let termsY = yPos + 5;
  termsLines.forEach(line => {
    pdf.text(line, pageWidth / 2 + 10, termsY);
    termsY += 5;
  });

  // Date field
  pdf.setFontSize(11);
  pdf.text('Date:', 20, yPos + 15);
  pdf.line(45, yPos + 15, 120, yPos + 15);

  // Footer for final page with page number and branding
  const footerY = pageHeight - 10;
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(150, 150, 150); // Light gray to be subtle
  
  // Add final page number if multiple pages exist
  if (currentPage > 1) {
    pdf.text(`Page ${currentPage}`, pageWidth / 2, footerY - 5, { align: 'center' });
  }
  
  pdf.text('Powered by Loystar', pageWidth / 2, footerY, { align: 'center' });

  // Download the PDF
  const fileName = `delivery-note-${orderNumber}.pdf`;
  pdf.save(fileName);
};

export default { generateDeliveryNotePDF };